import { useState } from "react";
import {
  acknowledgeFeedback,
  type InterventionFeedback,
} from "@/data/interventionFeedbackRepository";
import { useI18n } from "@/i18n/useI18n";

interface FeedbackPanelProps {
  feedback: InterventionFeedback[];
  /** Only the intervention's respondent can acknowledge messages. */
  canAcknowledge: boolean;
}

export function InterventionFeedbackPanel({ feedback, canAcknowledge }: FeedbackPanelProps) {
  const { t, locale } = useI18n();
  const [items, setItems] = useState(feedback);
  const [postponed, setPostponed] = useState<number[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState("");
  const pending = items.filter((entry) => entry.acknowledged_at === null);
  const visible = pending.filter((entry) => !postponed.includes(entry.id));
  const history = items.filter((entry) => entry.acknowledged_at !== null);

  const acknowledge = async (id: number) => {
    setBusy(id);
    setError("");
    try {
      await acknowledgeFeedback(id);
      const now = new Date().toISOString();
      setItems((current) =>
        current.map((entry) => entry.id === id ? { ...entry, acknowledged_at: now } : entry),
      );
    } catch (reason) {
      setError(`${t("feedbackAcknowledgeError")} (${String(reason)})`);
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) return null;
  return (
    <section className="intervention-feedback" aria-label={t("feedbackTitle")}>
      {pending.length > 0 && (
        <h3 className="intervention-feedback-heading">
          {t("feedbackPendingTitle").replace("{count}", String(pending.length))}
        </h3>
      )}
      {visible.map((entry) => (
        <article key={entry.id} className="intervention-feedback-item">
          <h4>{t("feedbackTitle")}</h4>
          <time dateTime={entry.created_at}>
            {new Date(entry.created_at).toLocaleString(locale)}
          </time>
          <p className="intervention-feedback-message">{entry.message}</p>
          {canAcknowledge && (
            <div className="intervention-feedback-actions">
              <button
                type="button"
                className="details-return-btn"
                onClick={() => setPostponed((current) => [...current, entry.id])}
              >
                {t("feedbackLater")}
              </button>
              <button
                type="button"
                className="details-save-btn"
                disabled={busy !== null}
                onClick={() => void acknowledge(entry.id)}
              >
                {t("feedbackAcknowledge")}
              </button>
            </div>
          )}
          {!canAcknowledge && <p className="intervention-feedback-meta">{t("feedbackAwaitingAcknowledgement")}</p>}
        </article>
      ))}
      {pending.length > 0 && visible.length === 0 && (
        <p className="intervention-feedback-meta">{t("feedbackRemainsPending")}</p>
      )}
      {error && <p role="alert" className="auth-gate-message error">{error}</p>}
      {history.length > 0 && (
        <details className="intervention-feedback-history">
          <summary>{t("feedbackPrevious").replace("{count}", String(history.length))}</summary>
          {history.map((entry) => (
            <article key={entry.id} className="intervention-feedback-past">
              <time dateTime={entry.created_at}>
                {new Date(entry.created_at).toLocaleString(locale)}
              </time>
              <p className="intervention-feedback-message">{entry.message}</p>
              <p className="intervention-feedback-meta">
                {t("feedbackAcknowledged")} · {new Date(entry.acknowledged_at!).toLocaleString(locale)}
              </p>
            </article>
          ))}
        </details>
      )}
    </section>
  );
}
